import React from 'react';
import { SlidersHorizontal } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'shadcn/components/ui/DropdownMenu';

/**
 * DisplayOptionsDropdown - Dropdown menu for display options (sorting, view settings, etc.)
 *
 * @param {Object} props
 * @param {string} props.label - Button label (e.g., "Display", "View")
 * @param {string} [props.menuTitle] - Menu header title (e.g., "Sort By")
 * @param {Array} props.options - Array of menu options
 * @param {string} props.options[].label - Option label
 * @param {string} props.options[].value - Option value
 * @param {string} [props.selectedValue] - Currently selected value
 * @param {Function} props.onChange - Callback when option is selected: (value) => void
 */
export function DisplayOptionsDropdown({ label, menuTitle, onChange, options = [], selectedValue }) {
    if (!label || !options.length) return null;

    const handleOptionClick = (value) => {
        if (onChange) {
            onChange(value);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" aria-label={label} className="gap-2">
                    <SlidersHorizontal size={16} aria-hidden="true" />
                    {label}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[240px]">
                {menuTitle && (
                    <>
                        <DropdownMenuLabel>{menuTitle}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                    </>
                )}
                {options.map((option) => {
                    const isSelected = selectedValue === option.value;
                    return (
                        <DropdownMenuItem
                            key={option.value}
                            onClick={() => handleOptionClick(option.value)}
                            className="cursor-pointer"
                        >
                            <span className="w-4 text-center mr-2">{isSelected ? '•' : ''}</span>
                            <span className={isSelected ? 'font-medium' : ''}>{option.label}</span>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
